/**
 * Deterministic orthogonal (elbow) routing for Chart AI arrows.
 *
 * tldraw elbow arrows only encode start/end sides + midpoint — not arbitrary
 * polylines. Candidates here are the families tldraw actually draws for a side
 * pair (facing Z, same-side U around the A∪B box, adjacent L) so the winner
 * round-trips into bindings.
 */

import {estimateMinEdgeLengthForLabel, LABEL_EDGE_PADDING} from './edgeLabelMetrics';
import {
    labelBoxesForPlacement,
    labelPlacementRoutePenalty,
    pickLabelPositionForEdge,
} from './edgeLabelPlacement';

export const ROUTING_SIDES = ['right', 'bottom', 'left', 'top'];
const OPPOSITE_SIDE = {right: 'left', left: 'right', top: 'bottom', bottom: 'top'};
const EDGE_TS = [0.28, 0.5, 0.72];
const MIDPOINTS = [0.2, 0.35, 0.5, 0.65, 0.8];
const DEFAULT_CLEARANCE = 28;
const NODE_HIT_PAD = 10;
const MIN_LABEL_SEP = 100;

export {estimateMinEdgeLengthForLabel};

/**
 * Shortest gap between two non-overlapping card rects (edge-to-edge clearance).
 *
 * @param {{x: number, y: number, w: number, h: number}} a
 * @param {{x: number, y: number, w: number, h: number}} b
 * @returns {number}
 */
export function minEdgeGapBetweenRects(a, b) {
    const horizontalGap = Math.max(b.x - (a.x + a.w), a.x - (b.x + b.w));
    const verticalGap = Math.max(b.y - (a.y + a.h), a.y - (b.y + b.h));
    if (horizontalGap > 0 && verticalGap <= 0) return horizontalGap;
    if (verticalGap > 0 && horizontalGap <= 0) return verticalGap;
    return Math.hypot(Math.max(horizontalGap, 0), Math.max(verticalGap, 0));
}
const ATTACHMENT_HINT_PENALTY = 80;
const TRAJECTORY_SIDE_PENALTY = 140;
const CORNER_MARGIN = 0.10;
const BEND_HINT_PENALTY = 1200;
const MIN_ATTACHMENT_ANGLE_DEG = 45;
const SHALLOW_ATTACHMENT_PENALTY = 2500;

/**
 * @param {{x: number, y: number, w: number, h: number}} rect
 * @param {number} pad
 * @returns {{x: number, y: number, w: number, h: number}}
 */
export function inflateRect(rect, pad) {
    return {
        x: rect.x - pad,
        y: rect.y - pad,
        w: rect.w + pad * 2,
        h: rect.h + pad * 2,
    };
}

/**
 * @param {{x: number, y: number, w: number, h: number}} a
 * @param {{x: number, y: number, w: number, h: number}} b
 * @returns {{x: number, y: number, w: number, h: number}}
 */
export function unionRects(a, b) {
    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);
    const right = Math.max(a.x + a.w, b.x + b.w);
    const bottom = Math.max(a.y + a.h, b.y + b.h);
    return {x, y, w: right - x, h: bottom - y};
}

/**
 * @param {{x: number, y: number, w: number, h: number}} a
 * @param {{x: number, y: number, w: number, h: number}} b
 * @param {number} [pad]
 * @returns {boolean}
 */
export function rectsOverlap(a, b, pad = 0) {
    return !(
        a.x + a.w + pad <= b.x
        || b.x + b.w + pad <= a.x
        || a.y + a.h + pad <= b.y
        || b.y + b.h + pad <= a.y
    );
}

function rectsNearlyEqual(a, b) {
    return Math.abs(a.x - b.x) < 1.5
        && Math.abs(a.y - b.y) < 1.5
        && Math.abs(a.w - b.w) < 1.5
        && Math.abs(a.h - b.h) < 1.5;
}

export function rectCenter(rect) {
    return {x: rect.x + rect.w / 2, y: rect.y + rect.h / 2};
}

/**
 * @param {{x: number, y: number, w: number, h: number}} rect
 * @param {'right'|'bottom'|'left'|'top'} side
 * @param {number} t — 0..1 along the edge
 * @returns {{x: number, y: number}}
 */
export function pointOnRectSide(rect, side, t) {
    const clamped = Math.min(1, Math.max(0, t));
    if (side === 'top') return {x: rect.x + clamped * rect.w, y: rect.y};
    if (side === 'bottom') return {x: rect.x + clamped * rect.w, y: rect.y + rect.h};
    if (side === 'left') return {x: rect.x, y: rect.y + clamped * rect.h};
    return {x: rect.x + rect.w, y: rect.y + clamped * rect.h};
}

/**
 * tldraw picks a manual elbow side from a non-center normalizedAnchor.
 * @param {'right'|'bottom'|'left'|'top'} side
 * @param {number} t
 * @returns {{x: number, y: number}}
 */
export function normalizedAnchorForSide(side, t) {
    const clamped = Math.min(0.92, Math.max(0.08, t));
    if (side === 'top') return {x: clamped, y: 0};
    if (side === 'bottom') return {x: clamped, y: 1};
    if (side === 'left') return {x: 0, y: clamped};
    return {x: 1, y: clamped};
}

/** Match tldraw `STROKE_SIZES` so parallel-gap math uses painted width. */
export const ARROW_STROKE_WIDTHS = {s: 2, m: 3.5, l: 5, xl: 10};
/** Empty gap between two parallel elbow strokes, in multiples of the thicker stroke. */
export const PARALLEL_FREE_SPACE_WIDTHS = 3;
const PARALLEL_OVERLAP_PX = 2;
/** Prefer a route that can actually host the label over a slightly shorter one. */

/**
 * Painted stroke width for a tldraw arrow size token.
 *
 * @param {string|null|undefined} size
 * @returns {number}
 */
export function arrowStrokeWidth(size) {
    return ARROW_STROKE_WIDTHS[size] ?? ARROW_STROKE_WIDTHS.s;
}

/**
 * Minimum empty space between two parallel strokes (not center-to-center).
 *
 * @param {number} widthA
 * @param {number} widthB
 * @returns {number}
 */
export function minParallelFreeSpace(widthA, widthB) {
    const a = Number(widthA) > 0 ? Number(widthA) : ARROW_STROKE_WIDTHS.s;
    const b = Number(widthB) > 0 ? Number(widthB) : ARROW_STROKE_WIDTHS.s;
    return PARALLEL_FREE_SPACE_WIDTHS * Math.max(a, b);
}

/**
 * Minimum centerline distance so the empty gap is {@link minParallelFreeSpace}.
 *
 * @param {number} widthA
 * @param {number} widthB
 * @returns {number}
 */
export function minParallelCenterDistance(widthA, widthB) {
    const a = Number(widthA) > 0 ? Number(widthA) : ARROW_STROKE_WIDTHS.s;
    const b = Number(widthB) > 0 ? Number(widthB) : ARROW_STROKE_WIDTHS.s;
    return (a + b) / 2 + minParallelFreeSpace(a, b);
}

/**
 * Minimum empty space from an edge stroke to a card it is only passing.
 *
 * @param {number} strokeWidth
 * @returns {number}
 */
export function minCardClearance(strokeWidth) {
    const width = Number(strokeWidth) > 0 ? Number(strokeWidth) : ARROW_STROKE_WIDTHS.s;
    return PARALLEL_FREE_SPACE_WIDTHS * width;
}

/**
 * Perpendicular gap between two axis-aligned parallel segments that overlap
 * along their run. Non-parallel or non-overlapping pairs return Infinity.
 *
 * @param {{x: number, y: number}} a1
 * @param {{x: number, y: number}} a2
 * @param {{x: number, y: number}} b1
 * @param {{x: number, y: number}} b2
 * @returns {number}
 */
export function parallelSegmentGap(a1, a2, b1, b2) {
    const axisA = segmentAxis(a1, a2);
    const axisB = segmentAxis(b1, b2);
    if (!axisA || axisA !== axisB) return Infinity;
    if (axisA === 'h') {
        if (overlap1D(a1.x, a2.x, b1.x, b2.x) <= PARALLEL_OVERLAP_PX) return Infinity;
        return Math.abs(a1.y - b1.y);
    }
    if (overlap1D(a1.y, a2.y, b1.y, b2.y) <= PARALLEL_OVERLAP_PX) return Infinity;
    return Math.abs(a1.x - b1.x);
}

/**
 * How many existing-edge segments sit closer than the 3×-thickness free-space rule.
 *
 * @param {Array<{x: number, y: number}>} points
 * @param {Array<{points?: Array<{x: number, y: number}>, strokeWidth?: number}>} otherEdges
 * @param {number} [strokeWidth]
 * @returns {number}
 */
/**
 * Tightest parallel overlap between two polylines and how far they still need
 * to separate so the empty gap is {@link minParallelFreeSpace}.
 *
 * @param {Array<{x: number, y: number}>} pointsA
 * @param {Array<{x: number, y: number}>} pointsB
 * @param {number} [strokeA]
 * @param {number} [strokeB]
 * @returns {{gap: number, axis: 'x'|'y', deficit: number, coordA: number, coordB: number}|null}
 */
export function worstParallelOverlap(pointsA, pointsB, strokeA = ARROW_STROKE_WIDTHS.s, strokeB = ARROW_STROKE_WIDTHS.s) {
    if (!pointsA || pointsA.length < 2 || !pointsB || pointsB.length < 2) return null;
    const required = minParallelFreeSpace(strokeA, strokeB);
    let worst = null;
    for (let i = 1; i < pointsA.length; i += 1) {
        for (let j = 1; j < pointsB.length; j += 1) {
            const gap = parallelSegmentGap(pointsA[i - 1], pointsA[i], pointsB[j - 1], pointsB[j]);
            if (!Number.isFinite(gap) || gap >= required - 1e-6) continue;
            const axis = segmentAxis(pointsA[i - 1], pointsA[i]) === 'v' ? 'x' : 'y';
            const coordA = axis === 'x' ? pointsA[i].x : pointsA[i].y;
            const coordB = axis === 'x' ? pointsB[j].x : pointsB[j].y;
            const deficit = required - gap;
            if (!worst || deficit > worst.deficit) {
                worst = {gap, axis, deficit, coordA, coordB};
            }
        }
    }
    return worst;
}

export function countParallelGapViolations(points, otherEdges, strokeWidth = ARROW_STROKE_WIDTHS.s) {
    if (!points || points.length < 2 || !otherEdges?.length) return 0;
    let hits = 0;
    for (const edge of otherEdges) {
        const other = edge.points || edge;
        if (!other || other.length < 2) continue;
        const otherWidth = edge.strokeWidth ?? ARROW_STROKE_WIDTHS.s;
        const required = minParallelCenterDistance(strokeWidth, otherWidth);
        for (let i = 1; i < points.length; i += 1) {
            for (let j = 1; j < other.length; j += 1) {
                const gap = parallelSegmentGap(points[i - 1], points[i], other[j - 1], other[j]);
                if (gap < required - 1e-6) hits += 1;
            }
        }
    }
    return hits;
}

/**
 * Closest side + distance of an axis-aligned segment passing a card.
 * Non-overlapping (no shared run) pairs return Infinity.
 *
 * @param {{x: number, y: number}} p
 * @param {{x: number, y: number}} q
 * @param {{x: number, y: number, w: number, h: number}} rect
 * @returns {{distance: number, side: 'left'|'right'|'top'|'bottom'|null}}
 */
export function segmentCardApproach(p, q, rect) {
    const axis = segmentAxis(p, q);
    if (axis === 'v') {
        const x = (p.x + q.x) / 2;
        const overlap = overlap1D(
            Math.min(p.y, q.y),
            Math.max(p.y, q.y),
            rect.y,
            rect.y + rect.h,
        );
        if (overlap <= PARALLEL_OVERLAP_PX) return {distance: Infinity, side: null};
        if (x < rect.x) return {distance: rect.x - x, side: 'left'};
        if (x > rect.x + rect.w) return {distance: x - (rect.x + rect.w), side: 'right'};
        return {distance: 0, side: null};
    }
    if (axis === 'h') {
        const y = (p.y + q.y) / 2;
        const overlap = overlap1D(
            Math.min(p.x, q.x),
            Math.max(p.x, q.x),
            rect.x,
            rect.x + rect.w,
        );
        if (overlap <= PARALLEL_OVERLAP_PX) return {distance: Infinity, side: null};
        if (y < rect.y) return {distance: rect.y - y, side: 'top'};
        if (y > rect.y + rect.h) return {distance: y - (rect.y + rect.h), side: 'bottom'};
        return {distance: 0, side: null};
    }
    return {distance: Infinity, side: null};
}

function cardExemptSides(card, options) {
    const exempt = new Set();
    if (options.startCard && rectsNearlyEqual(card, options.startCard) && options.fromSide) {
        exempt.add(options.fromSide);
    }
    if (options.endCard && rectsNearlyEqual(card, options.endCard) && options.toSide) {
        exempt.add(options.toSide);
    }
    return exempt;
}

/**
 * Worst R3 deficit for this polyline vs one card (attachment sides skipped).
 *
 * @param {Array<{x: number, y: number}>} points
 * @param {{x: number, y: number, w: number, h: number}} card
 * @param {{
 *   strokeWidth?: number,
 *   startCard?: object,
 *   endCard?: object,
 *   fromSide?: string,
 *   toSide?: string,
 * }} [options]
 * @returns {{distance: number, side: string|null, axis: 'x'|'y', sign: number, deficit: number}|null}
 */
export function worstCardClearance(points, card, options = {}) {
    if (!points || points.length < 2 || !card) return null;
    const minGap = minCardClearance(options.strokeWidth);
    const exempt = cardExemptSides(card, options);
    let worst = null;
    for (let i = 1; i < points.length; i += 1) {
        const approach = segmentCardApproach(points[i - 1], points[i], card);
        if (approach.distance >= minGap - 1e-6) continue;
        if (approach.side && exempt.has(approach.side)) continue;
        const axis = approach.side === 'left' || approach.side === 'right' || !approach.side
            ? 'x'
            : 'y';
        const sign = approach.side === 'left' || approach.side === 'top' ? 1 : -1;
        const deficit = minGap - approach.distance;
        if (!worst || deficit > worst.deficit) {
            worst = {distance: approach.distance, side: approach.side, axis, sign, deficit};
        }
    }
    return worst;
}

/**
 * How many cards sit closer than the 3×-stroke clearance rule (R3).
 *
 * @param {Array<{x: number, y: number}>} points
 * @param {Array<{x: number, y: number, w: number, h: number}>} cards
 * @param {{
 *   strokeWidth?: number,
 *   startCard?: object,
 *   endCard?: object,
 *   fromSide?: string,
 *   toSide?: string,
 * }} [options]
 * @returns {number}
 */
export function countCardClearanceViolations(points, cards, options = {}) {
    if (!points || points.length < 2 || !cards?.length) return 0;
    let hits = 0;
    for (const card of cards) {
        if (worstCardClearance(points, card, options)) hits += 1;
    }
    return hits;
}

function segmentAxis(p, q) {
    if (Math.abs(p.y - q.y) <= 0.5 && Math.abs(p.x - q.x) > 0.5) return 'h';
    if (Math.abs(p.x - q.x) <= 0.5 && Math.abs(p.y - q.y) > 0.5) return 'v';
    return null;
}

function overlap1D(a0, a1, b0, b1) {
    return Math.min(Math.max(a0, a1), Math.max(b0, b1)) - Math.max(Math.min(a0, a1), Math.min(b0, b1));
}

/**
 * Evenly spaced t values (0–1 along a card side).
 * Spread across the usable inset range so n>=2 arrows never share a point.
 *
 * @param {number} count
 * @param {{inset?: number}} [options]
 * @returns {number[]}
 */
export function spreadSideEntryTs(count, options = {}) {
    const n = Math.max(0, Math.floor(Number(count) || 0));
    if (n <= 0) return [];
    if (n === 1) return [0.5];

    const inset = options.inset ?? CORNER_MARGIN;
    const usable = Math.max(0, 1 - 2 * inset);
    const gap = usable / (n - 1);
    const start = inset;

    return Array.from({length: n}, (_, i) => (
        Math.round((start + i * gap) * 1e10) / 1e10
    ));
}

/**
 * Distinct `normalizedAnchor` values for `count` arrows on one card side.
 *
 * @param {'right'|'bottom'|'left'|'top'} side
 * @param {number} count
 * @param {{inset?: number}} [options]
 * @returns {Array<{x: number, y: number}>}
 */
export function spreadNormalizedAnchorsForSide(side, count, options) {
    return spreadSideEntryTs(count, options).map((t) => normalizedAnchorForSide(side, t));
}

export function sideFromNormalizedAnchor(anchor) {
    if (!anchor || typeof anchor.x !== 'number' || typeof anchor.y !== 'number') return null;
    if (Math.abs(anchor.x - 0.5) < 0.02 && Math.abs(anchor.y - 0.5) < 0.02) return null;
    if (Math.abs(anchor.x - 0.5) > Math.abs(anchor.y - 0.5) - 0.0001) {
        return anchor.x < 0.5 ? 'left' : 'right';
    }
    return anchor.y < 0.5 ? 'top' : 'bottom';
}

/**
 * @param {{x: number, y: number}|null|undefined} anchor
 * @returns {number}
 */
export function anchorTFromNormalized(anchor) {
    if (!anchor || typeof anchor.x !== 'number' || typeof anchor.y !== 'number') return 0.5;
    const side = sideFromNormalizedAnchor(anchor);
    if (side === 'left' || side === 'right') return anchor.y;
    if (side === 'top' || side === 'bottom') return anchor.x;
    return 0.5;
}

/**
 * Facing side from `from` toward `to` (larger axis delta wins; x biased).
 * @param {{x: number, y: number, w: number, h: number}} fromRect
 * @param {{x: number, y: number, w: number, h: number}} toRect
 * @returns {'right'|'bottom'|'left'|'top'}
 */
export function facingSide(fromRect, toRect) {
    const from = rectCenter(fromRect);
    const to = rectCenter(toRect);
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    if (Math.abs(dx) + 1 >= Math.abs(dy)) {
        return dx >= 0 ? 'right' : 'left';
    }
    return dy >= 0 ? 'bottom' : 'top';
}

/**
 * Preferred side on `toRect` for an edge arriving from `fromRect`.
 * On diagonal pairs, use the secondary axis so edges enter from above/below
 * rather than grazing the left/right when the source is mostly offset vertically.
 *
 * @param {{x: number, y: number, w: number, h: number}} toRect
 * @param {{x: number, y: number, w: number, h: number}} fromRect
 * @returns {'right'|'bottom'|'left'|'top'}
 */
export function preferredEnterSide(toRect, fromRect) {
    const from = rectCenter(fromRect);
    const to = rectCenter(toRect);
    const dx = from.x - to.x;
    const dy = from.y - to.y;
    if (Math.abs(dx) < Math.abs(dy)) {
        return dx >= 0 ? 'right' : 'left';
    }
    return dy >= 0 ? 'bottom' : 'top';
}

/**
 * Sort key for fan-in spokes sharing a hub — order sources along the primary
 * approach axis so higher/left sources route before lower/right ones.
 *
 * @param {{x: number, y: number, w: number, h: number}} fromRect
 * @param {{x: number, y: number, w: number, h: number}} toRect
 * @returns {number}
 */
export function fanInSourceSortKey(fromRect, toRect) {
    const from = rectCenter(fromRect);
    const to = rectCenter(toRect);
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    if (Math.abs(dx) >= Math.abs(dy)) {
        return from.y;
    }
    return from.x;
}

/**
 * Prefer arc bend sign that bows a fan-in spoke away from its siblings.
 *
 * @param {{x: number, y: number, w: number, h: number}} fromRect
 * @param {{x: number, y: number, w: number, h: number}} toRect
 * @returns {1|-1|null}
 */
export function preferredFanInBendSign(fromRect, toRect) {
    const from = rectCenter(fromRect);
    const to = rectCenter(toRect);
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    if (Math.abs(dx) >= Math.abs(dy)) {
        if (from.y < to.y - 6) return 1;
        if (from.y > to.y + 6) return -1;
        return null;
    }
    if (from.x < to.x - 6) return 1;
    if (from.x > to.x + 6) return -1;
    return null;
}

function collapsePoints(points) {
    const out = [];
    for (const point of points) {
        const last = out[out.length - 1];
        if (!last || Math.abs(last.x - point.x) > 0.5 || Math.abs(last.y - point.y) > 0.5) {
            out.push(point);
        }
    }
    return out;
}

/**
 * Liang–Barsky: true when the open segment cuts the inflated rect interior.
 * @param {{x: number, y: number}} p
 * @param {{x: number, y: number}} q
 * @param {{x: number, y: number, w: number, h: number}} rect
 * @param {number} [pad]
 * @returns {boolean}
 */
export function segmentIntersectsRect(p, q, rect, pad = NODE_HIT_PAD) {
    const r = inflateRect(rect, pad);
    const dx = q.x - p.x;
    const dy = q.y - p.y;
    let t0 = 0;
    let t1 = 1;
    const clips = [
        [-dx, p.x - r.x],
        [dx, r.x + r.w - p.x],
        [-dy, p.y - r.y],
        [dy, r.y + r.h - p.y],
    ];
    for (const [pClip, qClip] of clips) {
        if (pClip === 0) {
            if (qClip < 0) return false;
            continue;
        }
        const t = qClip / pClip;
        if (pClip < 0) {
            if (t > t1) return false;
            if (t > t0) t0 = t;
        } else {
            if (t < t0) return false;
            if (t < t1) t1 = t;
        }
    }
    return t1 - t0 > 1e-4;
}

function orient(a, b, c) {
    return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

/**
 * Proper (crossing) intersection — shared endpoints do not count.
 * @returns {boolean}
 */
export function segmentsIntersectProper(a1, a2, b1, b2) {
    const o1 = orient(a1, a2, b1);
    const o2 = orient(a1, a2, b2);
    const o3 = orient(b1, b2, a1);
    const o4 = orient(b1, b2, a2);
    return Math.sign(o1) * Math.sign(o2) < 0 && Math.sign(o3) * Math.sign(o4) < 0;
}

export function polylineLength(points) {
    let length = 0;
    for (let i = 1; i < points.length; i += 1) {
        length += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
    }
    return length;
}

export function pointAlongPolyline(points, t) {
    if (!points.length) return {x: 0, y: 0};
    if (points.length === 1) return points[0];
    const total = polylineLength(points);
    if (total <= 0) return points[0];
    let remain = total * Math.min(1, Math.max(0, t));
    for (let i = 1; i < points.length; i += 1) {
        const seg = Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
        if (remain <= seg) {
            const u = seg === 0 ? 0 : remain / seg;
            return {
                x: points[i - 1].x + (points[i].x - points[i - 1].x) * u,
                y: points[i - 1].y + (points[i].y - points[i - 1].y) * u,
            };
        }
        remain -= seg;
    }
    return points[points.length - 1];
}

function pointInRect(point, rect, pad = 0) {
    return point.x >= rect.x - pad
        && point.x <= rect.x + rect.w + pad
        && point.y >= rect.y - pad
        && point.y <= rect.y + rect.h + pad;
}

/**
 * @param {Array<{x: number, y: number}>} points
 * @param {Array<{x: number, y: number, w: number, h: number}>} obstacles
 * @returns {number}
 */
export function countNodeHits(points, obstacles) {
    let hits = 0;
    for (const rect of obstacles) {
        for (let i = 1; i < points.length; i += 1) {
            if (segmentIntersectsRect(points[i - 1], points[i], rect, NODE_HIT_PAD)) {
                hits += 1;
                break;
            }
        }
    }
    return hits;
}

/**
 * @param {Array<{x: number, y: number}>} points
 * @param {Array<{points: Array<{x: number, y: number}>}>} otherEdges
 * @returns {number}
 */
export function countEdgeCrossings(points, otherEdges) {
    let hits = 0;
    for (const edge of otherEdges || []) {
        const other = edge.points || edge;
        for (let i = 1; i < points.length; i += 1) {
            for (let j = 1; j < other.length; j += 1) {
                if (segmentsIntersectProper(points[i - 1], points[i], other[j - 1], other[j])) {
                    hits += 1;
                }
            }
        }
    }
    return hits;
}

/**
 * Default facing midpoints, plus lanes offset from existing parallel segments
 * by the 3×-thickness rule. Keeps the search small when the corridor is empty.
 *
 * @param {{x: number, y: number, w: number, h: number}} fromRect
 * @param {{x: number, y: number, w: number, h: number}} toRect
 * @param {'right'|'bottom'|'left'|'top'} fromSide
 * @param {number} strokeWidth
 * @param {Array<{points?: Array<{x: number, y: number}>, strokeWidth?: number}>} existingEdges
 * @returns {number[]}
 */
function facingMidpointSamples(fromRect, toRect, fromSide, strokeWidth, existingEdges, labelSize) {
    const start = pointOnRectSide(fromRect, fromSide, 0.5);
    const end = pointOnRectSide(toRect, OPPOSITE_SIDE[fromSide], 0.5);
    const horizontalRun = fromSide === 'right' || fromSide === 'left';
    const span = horizontalRun ? (end.x - start.x) : (end.y - start.y);
    const samples = new Set(MIDPOINTS);

    if (labelSize && (labelSize.w > 0 || labelSize.h > 0) && Math.abs(span) > 1) {
        // Bias elbowMidPoint so the hosting run grows enough to hold the collision box.
        const dim = horizontalRun ? labelSize.w : labelSize.h;
        const required = dim + LABEL_EDGE_PADDING * 2;
        const tFit = Math.min(0.85, Math.max(0.15, required / Math.abs(span)));
        samples.add(Math.round(tFit * 1e4) / 1e4);
        samples.add(Math.round((1 - tFit) * 1e4) / 1e4);
        samples.add(0.15);
        samples.add(0.85);
    }

    if (!existingEdges?.length) return [...samples].sort((a, b) => a - b);
    if (Math.abs(span) < 1) return [...samples].sort((a, b) => a - b);

    const extra = [];
    for (const edge of existingEdges) {
        const other = edge.points || edge;
        if (!other || other.length < 2) continue;
        const required = minParallelCenterDistance(strokeWidth, edge.strokeWidth ?? strokeWidth);
        for (let i = 1; i < other.length; i += 1) {
            const axis = segmentAxis(other[i - 1], other[i]);
            if (horizontalRun && axis !== 'v') continue;
            if (!horizontalRun && axis !== 'h') continue;
            const coord = horizontalRun ? other[i].x : other[i].y;
            const origin = horizontalRun ? start.x : start.y;
            for (const offset of [required, -required]) {
                const t = (coord + offset - origin) / span;
                if (t > 0.12 && t < 0.88) {
                    extra.push(Math.round(t * 1e4) / 1e4);
                }
            }
        }
    }
    extra.sort((a, b) => Math.abs(a - 0.5) - Math.abs(b - 0.5));
    for (const t of extra.slice(0, 6)) samples.add(t);
    return [...samples].sort((a, b) => a - b);
}

/**
 * Same scoring as placement: no-clear-slot and foreign-edge hits are expensive.
 *
 * @param {Array<{x: number, y: number}>} points
 * @param {string|null|undefined} label
 * @param {Array<object>} [existingEdges]
 * @param {Array<object>} [obstacles]
 * @param {Array<object>} [endpointCards]
 * @returns {number}
 */
function labelFitRoutePenalty(points, label, existingEdges, obstacles, endpointCards, alongSize) {
    return labelPlacementRoutePenalty(points, {
        label,
        labelSize: alongSize,
        otherEdges: existingEdges || [],
        cards: obstacles || [],
        endpointCards: endpointCards || [],
    });
}

function routeLabelOpts(points, {
    foreign,
    fromRect,
    toRect,
    existingEdges,
    labelPoints,
    label,
    labelBoxes,
    includeEndpoints = false,
}) {
    return pickLabelPositionForEdge(points, {
        obstacles: includeEndpoints && fromRect && toRect
            ? [...foreign, fromRect, toRect]
            : foreign,
        endpointCards: [
            fromRect && {...fromRect, endpoint: true},
            toRect && {...toRect, endpoint: true},
        ].filter(Boolean),
        labelPoints,
        label,
        labelBoxes,
        otherEdges: existingEdges || [],
    });
}

function hasForwardGap(fromRect, toRect, fromSide, clearance) {
    if (fromSide === 'right') {
        return toRect.x >= fromRect.x + fromRect.w + clearance * 0.35;
    }
    if (fromSide === 'left') {
        return toRect.x + toRect.w <= fromRect.x - clearance * 0.35;
    }
    if (fromSide === 'bottom') {
        return toRect.y >= fromRect.y + fromRect.h + clearance * 0.35;
    }
    return toRect.y + toRect.h <= fromRect.y - clearance * 0.35;
}

function routeFacing(start, end, fromSide, midPoint, fromRect, toRect, clearance, box) {
    if (!hasForwardGap(fromRect, toRect, fromSide, clearance)) {
        return routeSameSide(start, end, fromSide === 'right' || fromSide === 'left' ? 'bottom' : 'right', box);
    }
    if (fromSide === 'right' || fromSide === 'left') {
        const midX = start.x + (end.x - start.x) * midPoint;
        return collapsePoints([start, {x: midX, y: start.y}, {x: midX, y: end.y}, end]);
    }
    const midY = start.y + (end.y - start.y) * midPoint;
    return collapsePoints([start, {x: start.x, y: midY}, {x: end.x, y: midY}, end]);
}

function routeSameSide(start, end, side, box) {
    if (side === 'right') {
        const x = box.x + box.w;
        return collapsePoints([start, {x, y: start.y}, {x, y: end.y}, end]);
    }
    if (side === 'left') {
        const x = box.x;
        return collapsePoints([start, {x, y: start.y}, {x, y: end.y}, end]);
    }
    if (side === 'bottom') {
        const y = box.y + box.h;
        return collapsePoints([start, {x: start.x, y}, {x: end.x, y}, end]);
    }
    const y = box.y;
    return collapsePoints([start, {x: start.x, y}, {x: end.x, y}, end]);
}

function isOutward(side, from, to) {
    if (side === 'right') return to.x >= from.x - 1;
    if (side === 'left') return to.x <= from.x + 1;
    if (side === 'bottom') return to.y >= from.y - 1;
    return to.y <= from.y + 1;
}

function routeAdjacent(start, end, fromSide, toSide, box) {
    const hThenV = {x: end.x, y: start.y};
    const vThenH = {x: start.x, y: end.y};
    const options = [];
    if (isOutward(fromSide, start, hThenV)) {
        options.push(collapsePoints([start, hThenV, end]));
    }
    if (isOutward(fromSide, start, vThenH)) {
        options.push(collapsePoints([start, vThenH, end]));
    }
    if (!options.length) {
        return routeSameSide(start, end, fromSide, box);
    }
    if (options.length === 1) {
        return options[0];
    }
    let best = options[0];
    let bestAngle = minAttachmentAngle(best, fromSide, toSide);
    for (let i = 1; i < options.length; i += 1) {
        const angle = minAttachmentAngle(options[i], fromSide, toSide);
        if (angle > bestAngle) {
            bestAngle = angle;
            best = options[i];
        }
    }
    return best;
}

/**
 * Orthogonal polyline tldraw can realize for this side pair.
 *
 * @param {{x: number, y: number, w: number, h: number}} fromRect
 * @param {{x: number, y: number, w: number, h: number}} toRect
 * @param {{
 *   fromSide: string,
 *   toSide: string,
 *   fromT?: number,
 *   toT?: number,
 *   midPoint?: number,
 *   clearance?: number,
 * }} spec
 * @returns {Array<{x: number, y: number}>}
 */
export function buildElbowPolyline(fromRect, toRect, spec) {
    const fromSide = spec.fromSide;
    const toSide = spec.toSide;
    const fromT = spec.fromT ?? 0.5;
    const toT = spec.toT ?? 0.5;
    const midPoint = spec.midPoint ?? 0.5;
    const clearance = spec.clearance ?? DEFAULT_CLEARANCE;
    const start = pointOnRectSide(fromRect, fromSide, fromT);
    const end = pointOnRectSide(toRect, toSide, toT);
    const box = inflateRect(unionRects(fromRect, toRect), clearance);

    if (toSide === OPPOSITE_SIDE[fromSide]) {
        return routeFacing(start, end, fromSide, midPoint, fromRect, toRect, clearance, box);
    }
    if (fromSide === toSide) {
        return routeSameSide(start, end, fromSide, box);
    }
    return routeAdjacent(start, end, fromSide, toSide, box);
}

function routeScore(nodeHits, edgeHits, length, pointCount, extraFatal = 0) {
    return (nodeHits + extraFatal) * 1e6 + edgeHits * 1e3 + length + pointCount * 4;
}

function fatalRouteHits(route) {
    if (!route) return Infinity;
    return (route.nodeHits || 0) + (route.parallelHits || 0) + (route.cardClearanceHits || 0);
}

function sideTangent(side) {
    if (side === 'left' || side === 'right') return {x: 0, y: 1};
    return {x: 1, y: 0};
}

function vectorAngleDegrees(u, v) {
    const um = Math.hypot(u.x, u.y) || 1;
    const vm = Math.hypot(v.x, v.y) || 1;
    const dot = Math.abs(u.x * v.x + u.y * v.y) / (um * vm);
    return Math.acos(Math.min(1, dot)) * (180 / Math.PI);
}

function segmentVector(points, fromStart) {
    if (points.length < 2) return {x: 1, y: 0};
    if (fromStart) {
        return {x: points[1].x - points[0].x, y: points[1].y - points[0].y};
    }
    const n = points.length;
    return {x: points[n - 1].x - points[n - 2].x, y: points[n - 1].y - points[n - 2].y};
}

/**
 * Angle between the edge segment at an attachment end and the card side (90° = perpendicular).
 *
 * @param {Array<{x: number, y: number}>} points
 * @param {'right'|'bottom'|'left'|'top'} side
 * @param {boolean} atStart
 * @returns {number}
 */
export function attachmentAngleAtSide(points, side, atStart) {
    return vectorAngleDegrees(segmentVector(points, atStart), sideTangent(side));
}

/**
 * @param {Array<{x: number, y: number}>} points
 * @param {'right'|'bottom'|'left'|'top'} fromSide
 * @param {'right'|'bottom'|'left'|'top'} toSide
 * @returns {number}
 */
export function minAttachmentAngle(points, fromSide, toSide) {
    if (points.length < 2) return 0;
    return Math.min(
        attachmentAngleAtSide(points, fromSide, true),
        attachmentAngleAtSide(points, toSide, false),
    );
}

/**
 * @param {number} t — 0..1 along card side
 * @param {number} [margin]
 * @returns {boolean}
 */
export function isCornerAttachment(t, margin = CORNER_MARGIN) {
    return t < margin || t > 1 - margin;
}

function attachmentQualityPenalty(points, fromSide, toSide, fromT, toT) {
    const minAngle = minAttachmentAngle(points, fromSide, toSide);
    let penalty = 0;
    if (minAngle < MIN_ATTACHMENT_ANGLE_DEG) {
        penalty += (MIN_ATTACHMENT_ANGLE_DEG - minAngle) * SHALLOW_ATTACHMENT_PENALTY;
    }
    // Prefer larger angles when obstacle/crossing scores tie.
    penalty -= minAngle * 0.05;
    return penalty;
}

function routeMeetsAttachmentQuality(route) {
    if (!route) return false;
    const minAngle = route.minAngle ?? minAttachmentAngle(route.points, route.fromSide, route.toSide);
    return minAngle >= MIN_ATTACHMENT_ANGLE_DEG
        && !isCornerAttachment(route.fromT)
        && !isCornerAttachment(route.toT);
}

function isBetterAttachmentCandidate(next, prev, fromRect, toRect) {
    if (!prev) return true;
    const nextFatal = fatalRouteHits(next);
    const prevFatal = fatalRouteHits(prev);
    if (nextFatal !== prevFatal) return nextFatal < prevFatal;
    if (next.edgeHits !== prev.edgeHits) return next.edgeHits < prev.edgeHits;
    const enter = preferredEnterSide(toRect, fromRect);
    const depart = facingSide(fromRect, toRect);
    const nextSideScore = (next.fromSide === depart ? 0 : 1) + (next.toSide === enter ? 0 : 1);
    const prevSideScore = (prev.fromSide === depart ? 0 : 1) + (prev.toSide === enter ? 0 : 1);
    if (nextSideScore !== prevSideScore) return nextSideScore < prevSideScore;
    if (next.minAngle !== prev.minAngle) return next.minAngle > prev.minAngle;
    return next.length < prev.length;
}

/**
 * Build the estimated on-canvas rect for a label centered at a point.
 *
 * @param {{x: number, y: number}} point
 * @param {{w: number, h: number}} size
 * @returns {{x: number, y: number, w: number, h: number}}
 */
export function labelBoxAt(point, size) {
    return {
        x: point.x - size.w / 2,
        y: point.y - size.h / 2,
        w: size.w,
        h: size.h,
    };
}

/**
 * Choose label t in [0.15, 0.85] away from other labels and card interiors.
 *
 * When `label` and `labelBoxes` are supplied, candidates are scored against
 * true rectangle overlap (this label's estimated text footprint vs. every
 * other placed label's footprint) rather than a fixed-radius point distance —
 * a long label sitting 90px from another long label still collides even
 * though two short labels at that distance would not. This is the same
 * "slide the label along the arrow" mechanic tldraw exposes as a manual drag
 * (arrow `labelPosition`, 0 = start .. 1 = end), just driven automatically.
 *
 * @param {Array<{x: number, y: number}>} points
 * @param {{
 *   obstacles?: Array<object>,
 *   labelPoints?: Array<{x: number, y: number}>,
 *   label?: string|null,
 *   labelBoxes?: Array<{x: number, y: number, w: number, h: number}>,
 * }} [options]
 * @returns {number}
 */
export function pickLabelPosition(points, options = {}) {
    return pickLabelPositionForEdge(points, {
        label: options.label,
        cards: options.obstacles || [],
        placedLabelBoxes: options.labelBoxes || [],
        labelPoints: options.labelPoints || [],
        otherEdges: options.otherEdges || [],
    });
}

/**
 * Pick the tldraw-encodable elbow with fewest card hits, then fewest edge
 * crossings, then shortest path.
 *
 * @param {{
 *   fromRect: {x: number, y: number, w: number, h: number},
 *   toRect: {x: number, y: number, w: number, h: number},
 *   obstacles?: Array<{x: number, y: number, w: number, h: number}>,
 *   existingEdges?: Array<{points: Array<{x: number, y: number}>, labelPoint?: {x: number, y: number}, strokeWidth?: number}>,
 *   clearance?: number,
 *   strokeWidth?: number,
 * }} args
 * @returns {{
 *   fromSide: string,
 *   toSide: string,
 *   fromT: number,
 *   toT: number,
 *   elbowMidPoint: number,
 *   labelPosition: number,
 *   points: Array<{x: number, y: number}>,
 *   nodeHits: number,
 *   edgeHits: number,
 *   parallelHits: number,
 *   cardClearanceHits: number,
 *   length: number,
 * }}
 */
export function pickBestArrowRoute({
    fromRect,
    toRect,
    obstacles = [],
    existingEdges = [],
    clearance = DEFAULT_CLEARANCE,
    preferredFromT,
    preferredToT,
    label = null,
    strokeWidth = ARROW_STROKE_WIDTHS.s,
}) {
    const foreign = (obstacles || []).filter(
        (rect) => !rectsNearlyEqual(rect, fromRect) && !rectsNearlyEqual(rect, toRect)
    );
    const labelPoints = (existingEdges || [])
        .map((edge) => edge.labelPoint)
        .filter(Boolean);
    const labelBoxes = (existingEdges || [])
        .map((edge) => edge.labelBox)
        .filter(Boolean);
    const alongSize = label ? labelBoxesForPlacement(label).alongSegmentSize : null;

    let best = null;
    let bestScore = Infinity;
    let bestCompliant = null;

    for (const fromSide of ROUTING_SIDES) {
        for (const toSide of ROUTING_SIDES) {
            for (const fromT of EDGE_TS) {
                for (const toT of EDGE_TS) {
                    if (isCornerAttachment(fromT) || isCornerAttachment(toT)) continue;
                    const midSamples = fromSide === OPPOSITE_SIDE[toSide]
                        ? facingMidpointSamples(
                            fromRect,
                            toRect,
                            fromSide,
                            strokeWidth,
                            existingEdges,
                            alongSize,
                        )
                        : [0.5];
                    for (const midPoint of midSamples) {
                        const points = buildElbowPolyline(fromRect, toRect, {
                            fromSide,
                            toSide,
                            fromT,
                            toT,
                            midPoint,
                            clearance,
                        });
                        if (points.length < 2) continue;
                        const nodeHits = countNodeHits(points, foreign);
                        const edgeHits = countEdgeCrossings(points, existingEdges);
                        const parallelHits = countParallelGapViolations(
                            points,
                            existingEdges,
                            strokeWidth,
                        );
                        const cardClearanceHits = countCardClearanceViolations(
                            points,
                            [...foreign, fromRect, toRect],
                            {
                                strokeWidth,
                                startCard: fromRect,
                                endCard: toRect,
                                fromSide,
                                toSide,
                            },
                        );
                        const length = polylineLength(points);
                        const minAngle = minAttachmentAngle(points, fromSide, toSide);
                        let attachmentPenalty = 0;
                        const departSide = facingSide(fromRect, toRect);
                        const arriveSide = facingSide(toRect, fromRect);
                        if (fromSide !== departSide) {
                            attachmentPenalty += TRAJECTORY_SIDE_PENALTY;
                        }
                        if (toSide !== arriveSide && toSide !== OPPOSITE_SIDE[departSide]) {
                            attachmentPenalty += TRAJECTORY_SIDE_PENALTY * 0.6;
                        }
                        if (preferredFromT != null && Math.abs(fromT - preferredFromT) > 0.01) {
                            attachmentPenalty += ATTACHMENT_HINT_PENALTY;
                        }
                        if (preferredToT != null && Math.abs(toT - preferredToT) > 0.01) {
                            attachmentPenalty += ATTACHMENT_HINT_PENALTY;
                        }
                        const qualityPenalty = attachmentQualityPenalty(
                            points,
                            fromSide,
                            toSide,
                            fromT,
                            toT,
                        );
                        const score = routeScore(
                            nodeHits,
                            edgeHits,
                            length,
                            points.length,
                            parallelHits + cardClearanceHits,
                        )
                            + attachmentPenalty
                            + qualityPenalty
                            + labelFitRoutePenalty(points, label, existingEdges, foreign, [fromRect, toRect], alongSize);
                        const candidate = {
                            fromSide,
                            toSide,
                            fromT,
                            toT,
                            elbowMidPoint: midPoint,
                            points,
                            nodeHits,
                            edgeHits,
                            parallelHits,
                            cardClearanceHits,
                            length,
                            minAngle,
                        };
                        if (score < bestScore) {
                            bestScore = score;
                            best = candidate;
                        }
                        if (minAngle >= MIN_ATTACHMENT_ANGLE_DEG
                            && isBetterAttachmentCandidate(candidate, bestCompliant, fromRect, toRect)) {
                            bestCompliant = candidate;
                        }
                    }
                }
            }
        }
    }

    // A "compliant" (good attachment angle) candidate is only worth taking
    // over the raw score-loop winner when it doesn't cut through *more*
    // cards — otherwise a perpendicular line that passes straight through an
    // unrelated card (common in a straightened ELK hierarchy column) would
    // always beat a genuine detour just because the detour's shallower
    // attachment angle kept it out of `bestCompliant` contention.
    if (bestCompliant && (!best || fatalRouteHits(bestCompliant) <= fatalRouteHits(best))) {
        best = bestCompliant;
    }

    if (!best) {
        const fromSide = facingSide(fromRect, toRect);
        const toSide = preferredEnterSide(toRect, fromRect);
        const fromT = 0.5;
        const toT = 0.5;
        const points = buildElbowPolyline(fromRect, toRect, {fromSide, toSide, fromT, toT});
        best = {
            fromSide,
            toSide,
            fromT,
            toT,
            elbowMidPoint: 0.5,
            points,
            nodeHits: countNodeHits(points, foreign),
            edgeHits: countEdgeCrossings(points, existingEdges),
            parallelHits: countParallelGapViolations(points, existingEdges, strokeWidth),
            cardClearanceHits: countCardClearanceViolations(
                points,
                [...foreign, fromRect, toRect],
                {
                    strokeWidth,
                    startCard: fromRect,
                    endCard: toRect,
                    fromSide,
                    toSide,
                },
            ),
            length: polylineLength(points),
            minAngle: minAttachmentAngle(points, fromSide, toSide),
        };
    }

    best.labelPosition = routeLabelOpts(best.points, {
        foreign,
        fromRect,
        toRect,
        existingEdges,
        labelPoints,
        label,
        labelBoxes,
    });
    return best;
}

/**
 * Count existing edge segments that pass through a candidate card interior.
 *
 * @param {{x: number, y: number, w: number, h: number}} rect
 * @param {Array<{points: Array<{x: number, y: number}>}>} existingEdges
 * @returns {number}
 */
export function countCardInteriorEdgeHits(rect, existingEdges) {
    if (!rect || !existingEdges?.length) return 0;
    let hits = 0;
    for (const edge of existingEdges) {
        const points = edge?.points;
        if (!points || points.length < 2) continue;
        for (let i = 0; i < points.length - 1; i += 1) {
            if (segmentIntersectsRect(points[i], points[i + 1], rect, NODE_HIT_PAD)) {
                hits += 1;
                break;
            }
        }
    }
    return hits;
}

/**
 * Place a new card around connection target(s) so a short, unobstructed elbow is possible.
 *
 * @param {{
 *   hubRect: {x: number, y: number, w: number, h: number},
 *   targetRects?: Array<{x: number, y: number, w: number, h: number}>,
 *   occupied: Array<{x: number, y: number, w: number, h: number}>,
 *   cardW: number,
 *   cardH: number,
 *   gap: number,
 *   collisionPad: number,
 *   existingEdges?: Array<{points: Array<{x: number, y: number}>}>,
 *   addIndex?: number,
 *   maxRings?: number,
 * }} args
 * @returns {{x: number, y: number}}
 */
/**
 * @param {Array<{x: number, y: number, w: number, h: number}>} rects
 * @returns {{x: number, y: number}}
 */
export function centroidOfRects(rects) {
    if (!rects?.length) return {x: 0, y: 0};
    let sx = 0;
    let sy = 0;
    for (const rect of rects) {
        const c = rectCenter(rect);
        sx += c.x;
        sy += c.y;
    }
    return {x: sx / rects.length, y: sy / rects.length};
}

function scoreCardCenterCandidate({
    center,
    cardW,
    cardH,
    targetRects,
    occupied,
    existingEdges,
    collisionPad,
    ring,
    minEdgeLength = 0,
    preferAdjacent = true,
}) {
    const rect = {
        x: center.x - cardW / 2,
        y: center.y - cardH / 2,
        w: cardW,
        h: cardH,
    };
    if ((occupied || []).some((other) => rectsOverlap(rect, other, collisionPad))) {
        return null;
    }
    if (countCardInteriorEdgeHits(rect, existingEdges) > 0) {
        return null;
    }
    let score = ring * 48;
    let shortestGap = Infinity;
    for (const targetRect of targetRects) {
        const route = pickBestArrowRoute({
            fromRect: rect,
            toRect: targetRect,
            obstacles: occupied,
            existingEdges,
        });
        shortestGap = Math.min(shortestGap, minEdgeGapBetweenRects(rect, targetRect));
        score += routeScore(route.nodeHits, route.edgeHits, route.length, route.points.length);
    }
    if (minEdgeLength > 0 && shortestGap < minEdgeLength) {
        return null;
    }
    if (preferAdjacent && targetRects.length === 1 && ring === 1) {
        const targetC = rectCenter(targetRects[0]);
        const horizontalNeighbor = Math.abs(center.x - targetC.x) > Math.abs(center.y - targetC.y);
        if (horizontalNeighbor) score -= 32;
    }
    return {center, score};
}

function minRingForEdgeLength(minEdgeLength, cardW, gap) {
    if (!minEdgeLength || minEdgeLength <= 0) return 1;
    const step = cardW + gap;
    return Math.max(1, Math.ceil((minEdgeLength + cardW) / step));
}

const RING_DIRS_BELOW_FIRST = [
    [0, 1],
    [-1, 1],
    [1, 1],
    [-1, 0],
    [1, 0],
    [-1, -1],
    [1, -1],
    [0, -1],
];

const RING_DIRS_ABOVE_FIRST = [
    [0, -1],
    [-1, -1],
    [1, -1],
    [-1, 0],
    [1, 0],
    [-1, 1],
    [1, 1],
    [0, 1],
];

export function pickBestCardCenter({
    hubRect,
    targetRects = null,
    occupied,
    cardW,
    cardH,
    gap,
    collisionPad,
    existingEdges = [],
    addIndex = 0,
    maxRings = 6,
    searchFrom = 'below',
    minEdgeLength = 0,
}) {
    const stepX = cardW + gap;
    const stepY = cardH + gap;
    const targets = targetRects?.length ? targetRects : [hubRect];
    const hubC = targets.length === 1 ? rectCenter(targets[0]) : centroidOfRects(targets);
    const dirs = searchFrom === 'above' ? RING_DIRS_ABOVE_FIRST : RING_DIRS_BELOW_FIRST;
    const minRing = minRingForEdgeLength(minEdgeLength, cardW, gap);
    const preferAdjacent = minEdgeLength <= 0;

    for (let ring = minRing; ring <= maxRings; ring += 1) {
        for (const [dx, dy] of dirs) {
            const center = {
                x: hubC.x + dx * ring * stepX,
                y: hubC.y + dy * ring * stepY,
            };
            const scored = scoreCardCenterCandidate({
                center,
                cardW,
                cardH,
                targetRects: targets,
                occupied,
                existingEdges,
                collisionPad,
                ring,
                minEdgeLength,
                preferAdjacent,
            });
            if (scored) {
                return scored.center;
            }
        }
    }

    return {
        x: hubC.x + (Math.max(maxRings, minRing) + 1 + addIndex) * stepX,
        y: hubC.y,
    };
}

const ARC_STAGGER_TS = [0.32, 0.5, 0.68];
const ARC_BEND_SIGNS = [1, -1];
const ARC_BEND_FACTORS = [0.18, 0.32, 0.48, 0.68, 0.9, 1.15, 1.4];
const ARC_SAMPLES = 24;
const ROUTE_CLEARANCE_STEPS = [28, 48, 64, 96, 128, 160, 200, 260, 320];

function computeArcBendMagnitude(dist, factor, fromRect, toRect) {
    const fromC = rectCenter(fromRect);
    const toC = rectCenter(toRect);
    const span = Math.hypot(toC.x - fromC.x, toC.y - fromC.y);
    const maxBend = Math.min(640, Math.max(80, span * 0.85, dist * 0.65));
    return Math.min(maxBend, Math.max(28, dist * factor));
}

/**
 * Sample the TLDraw arc: quadratic through start / bend-offset midpoint / end.
 * `bend` matches tldraw `shape.props.bend` (perpendicular offset of the mid handle).
 *
 * @param {{x: number, y: number}} start
 * @param {{x: number, y: number}} end
 * @param {number} bend
 * @param {number} [samples]
 * @returns {Array<{x: number, y: number}>}
 */
export function sampleArcPoints(start, end, bend, samples = ARC_SAMPLES) {
    const med = {x: (start.x + end.x) / 2, y: (start.y + end.y) / 2};
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const dist = Math.hypot(dx, dy) || 1;
    const ux = dx / dist;
    const uy = dy / dist;
    const middle = {
        x: med.x + (-uy) * (-bend),
        y: med.y + ux * (-bend),
    };
    const points = [];
    const steps = Math.max(2, samples);
    for (let i = 0; i <= steps; i += 1) {
        const t = i / steps;
        const mt = 1 - t;
        points.push({
            x: mt * mt * start.x + 2 * mt * t * middle.x + t * t * end.x,
            y: mt * mt * start.y + 2 * mt * t * middle.y + t * t * end.y,
        });
    }
    return points;
}

/**
 * @param {{x: number, y: number, w: number, h: number}} fromRect
 * @param {{x: number, y: number, w: number, h: number}} toRect
 * @param {{fromSide: string, toSide: string, fromT?: number, toT?: number, bend: number, samples?: number}} spec
 * @returns {Array<{x: number, y: number}>}
 */
export function buildArcPolyline(fromRect, toRect, spec) {
    const start = pointOnRectSide(fromRect, spec.fromSide, spec.fromT ?? 0.5);
    const end = pointOnRectSide(toRect, spec.toSide, spec.toT ?? 0.5);
    return sampleArcPoints(start, end, spec.bend, spec.samples ?? ARC_SAMPLES);
}

const ARC_SIDE_PAIRS = [
    ['bottom', 'top'],
    ['top', 'bottom'],
    ['left', 'right'],
    ['right', 'left'],
    ['bottom', 'left'],
    ['bottom', 'right'],
    ['top', 'left'],
    ['top', 'right'],
    ['left', 'top'],
    ['left', 'bottom'],
    ['right', 'top'],
    ['right', 'bottom'],
];

const ARC_DETOUR_SIDE_PAIRS = [
    ['bottom', 'bottom'],
    ['top', 'top'],
    ['left', 'left'],
    ['right', 'right'],
];

const STANDARD_ARC_BEND_FACTORS = [0.18, 0.32, 0.48, 0.68];
const EXTENDED_ARC_BEND_FACTORS = [0.18, 0.32, 0.48, 0.68, 0.9, 1.15, 1.4];

function standardArcBend(dist, factor) {
    return Math.min(160, Math.max(28, dist * factor));
}

export function countEndpointBodyHits(points, fromRect, toRect) {
    if (!points?.length) return 0;
    const innerFrom = inflateRect(fromRect, -20);
    const innerTo = inflateRect(toRect, -20);
    let hits = 0;
    for (let i = 1; i < points.length - 1; i += 1) {
        const point = points[i];
        if (innerFrom.w > 0 && innerFrom.h > 0 && pointInRect(point, innerFrom)) {
            hits += 1;
        } else if (innerTo.w > 0 && innerTo.h > 0 && pointInRect(point, innerTo)) {
            hits += 1;
        }
        if (hits > 0) break;
    }
    return hits;
}

function searchArcRoutes({
    fromRect,
    toRect,
    foreign,
    existingEdges,
    pairs,
    factors,
    bendForFactor,
    fromTHint,
    toTHint,
    penalizeEndpointInteriors = false,
    preferredBendSign = null,
    label = null,
    alongSize = null,
}) {
    const labelAlong = alongSize || (label ? labelBoxesForPlacement(label).alongSegmentSize : null);
    const ts = [fromTHint, 0.5, ...ARC_STAGGER_TS];
    let best = null;
    let bestScore = Infinity;
    let bestCompliant = null;

    for (const [fromSide, toSide] of pairs) {
        for (const fromT of ts) {
            for (const toT of ts) {
                if (isCornerAttachment(fromT) || isCornerAttachment(toT)) continue;
                const start = pointOnRectSide(fromRect, fromSide, fromT);
                const end = pointOnRectSide(toRect, toSide, toT);
                const dist = Math.hypot(end.x - start.x, end.y - start.y) || 1;
                for (const sign of ARC_BEND_SIGNS) {
                    for (const factor of factors) {
                        const bend = sign * bendForFactor(dist, factor, fromRect, toRect);
                        const points = sampleArcPoints(start, end, bend);
                        const nodeHits = countNodeHits(points, foreign);
                        const endpointHits = penalizeEndpointInteriors
                            ? countEndpointBodyHits(points, fromRect, toRect)
                            : 0;
                        const edgeHits = countEdgeCrossings(points, existingEdges);
                        const length = polylineLength(points);
                        const minAngle = minAttachmentAngle(points, fromSide, toSide);
                        const qualityPenalty = attachmentQualityPenalty(
                            points,
                            fromSide,
                            toSide,
                            fromT,
                            toT,
                        );
                        let score = routeScore(
                            nodeHits + endpointHits,
                            edgeHits,
                            length,
                            points.length,
                        ) + qualityPenalty + labelFitRoutePenalty(
                            points,
                            label,
                            existingEdges,
                            foreign,
                            [fromRect, toRect],
                            labelAlong,
                        );
                        if (preferredBendSign != null) {
                            const sign = bend >= 0 ? 1 : -1;
                            if (sign !== preferredBendSign) {
                                score += BEND_HINT_PENALTY;
                            }
                        }
                        const candidate = {
                            fromSide,
                            toSide,
                            fromT,
                            toT,
                            bend,
                            labelPosition: 0.5,
                            points,
                            nodeHits,
                            endpointHits,
                            edgeHits,
                            length,
                            minAngle,
                        };
                        if (score < bestScore) {
                            bestScore = score;
                            best = candidate;
                        }
                        if (minAngle >= MIN_ATTACHMENT_ANGLE_DEG
                            && !isCornerAttachment(fromT)
                            && !isCornerAttachment(toT)
                            && isBetterAttachmentCandidate(candidate, bestCompliant, fromRect, toRect)) {
                            bestCompliant = candidate;
                        }
                    }
                }
            }
        }
    }

    return bestCompliant || best;
}

function totalArcRouteHits(route) {
    if (!route) return Infinity;
    return (route.nodeHits || 0) + (route.endpointHits || 0);
}

/**
 * Curved (arc) route for incremental chart edges. Tries facing + alternate
 * side/bend combinations so the stroke misses card rects when a detour exists.
 *
 * @param {{
 *   fromRect: {x: number, y: number, w: number, h: number},
 *   toRect: {x: number, y: number, w: number, h: number},
 *   obstacles?: Array<{x: number, y: number, w: number, h: number}>,
 *   existingEdges?: Array<{points: Array<{x: number, y: number}>}>,
 *   arcIndex?: number,
 * }} params
 * @returns {{
 *   fromSide: 'right'|'bottom'|'left'|'top',
 *   toSide: 'right'|'bottom'|'left'|'top',
 *   fromT: number,
 *   toT: number,
 *   bend: number,
 *   labelPosition: number,
 *   points: Array<{x: number, y: number}>,
 *   nodeHits: number,
 *   edgeHits: number,
 *   length: number,
 * }}
 */
export function pickArcArrowRoute({
    fromRect,
    toRect,
    obstacles = [],
    existingEdges = [],
    arcIndex = 0,
    allowWideDetour = false,
    preferredBendSign = null,
    preferredFromT,
    preferredToT,
    label = null,
}) {
    const foreign = (obstacles || []).filter(
        (rect) => !rectsNearlyEqual(rect, fromRect) && !rectsNearlyEqual(rect, toRect)
    );
    const labelPoints = (existingEdges || [])
        .map((edge) => edge.labelPoint)
        .filter(Boolean);
    const labelBoxes = (existingEdges || [])
        .map((edge) => edge.labelBox)
        .filter(Boolean);
    const facing = facingSide(fromRect, toRect);
    const staggerT = ARC_STAGGER_TS[arcIndex % ARC_STAGGER_TS.length];
    const fromTHint = preferredFromT ?? staggerT;
    const toTHint = preferredToT ?? ARC_STAGGER_TS[(arcIndex + 1) % ARC_STAGGER_TS.length];
    const bendSign = preferredBendSign ?? preferredFanInBendSign(fromRect, toRect);
    const alongSize = label ? labelBoxesForPlacement(label).alongSegmentSize : null;

    let best = searchArcRoutes({
        fromRect,
        toRect,
        foreign,
        existingEdges,
        pairs: [[facing, preferredEnterSide(toRect, fromRect)], [facing, OPPOSITE_SIDE[facing]], ...ARC_SIDE_PAIRS],
        factors: STANDARD_ARC_BEND_FACTORS,
        bendForFactor: (dist, factor) => standardArcBend(dist, factor),
        fromTHint,
        toTHint,
        penalizeEndpointInteriors: false,
        preferredBendSign: bendSign,
        label,
        alongSize,
    });

    const endpointPenetration = allowWideDetour
        ? countEndpointBodyHits(best?.points, fromRect, toRect)
        : 0;
    if (allowWideDetour && best && (best.nodeHits > 0 || endpointPenetration > 0)) {
        const detour = searchArcRoutes({
            fromRect,
            toRect,
            foreign,
            existingEdges,
            pairs: [...ARC_DETOUR_SIDE_PAIRS, [facing, OPPOSITE_SIDE[facing]], ...ARC_SIDE_PAIRS],
            factors: EXTENDED_ARC_BEND_FACTORS,
            bendForFactor: (dist, factor, from, to) => computeArcBendMagnitude(dist, factor, from, to),
            fromTHint,
            toTHint,
            penalizeEndpointInteriors: true,
            preferredBendSign: bendSign,
            label,
            alongSize,
        });
        const bestTotal = totalArcRouteHits(best) + endpointPenetration;
        const detourTotal = totalArcRouteHits(detour);
        if (detour && (
            detourTotal < bestTotal
            || (detourTotal === bestTotal && detour.edgeHits < best.edgeHits)
        )) {
            best = detour;
        }
    }

    if (!best) {
        const fromSide = facing;
        const toSide = preferredEnterSide(toRect, fromRect);
        const fromT = isCornerAttachment(fromTHint) ? 0.5 : fromTHint;
        const toT = isCornerAttachment(toTHint) ? 0.5 : toTHint;
        const start = pointOnRectSide(fromRect, fromSide, fromT);
        const end = pointOnRectSide(toRect, toSide, toT);
        const dist = Math.hypot(end.x - start.x, end.y - start.y) || 1;
        const sign = bendSign
            ?? ARC_BEND_SIGNS[Math.floor(arcIndex / ARC_STAGGER_TS.length) % ARC_BEND_SIGNS.length];
        const bend = sign * computeArcBendMagnitude(dist, 0.32, fromRect, toRect);
        const points = sampleArcPoints(start, end, bend);
        best = {
            fromSide,
            toSide,
            fromT,
            toT,
            bend,
            labelPosition: routeLabelOpts(points, {
                foreign,
                fromRect,
                toRect,
                existingEdges,
                labelPoints,
                label,
                labelBoxes,
            }),
            points,
            nodeHits: countNodeHits(points, foreign),
            endpointHits: countEndpointBodyHits(points, fromRect, toRect),
            edgeHits: countEdgeCrossings(points, existingEdges),
            length: polylineLength(points),
        };
    }

    if (best && (allowWideDetour || label)) {
        best.labelPosition = routeLabelOpts(best.points, {
            foreign,
            fromRect,
            toRect,
            existingEdges,
            labelPoints,
            label,
            labelBoxes,
            includeEndpoints: allowWideDetour,
        });
    }

    return best;
}

/**
 * Prefer a straight arc (bend=0); curve only when the segment hits foreign cards.
 *
 * @param {{
 *   fromRect: {x: number, y: number, w: number, h: number},
 *   toRect: {x: number, y: number, w: number, h: number},
 *   obstacles?: Array<{x: number, y: number, w: number, h: number}>,
 *   existingEdges?: Array<{points: Array<{x: number, y: number}>}>,
 *   arcIndex?: number,
 *   preferredFromT?: number,
 *   preferredToT?: number,
 *   preferredBendSign?: number|null,
 * }} params
 * @returns {{kind: 'arc', route: object}}
 */
function pointToSegmentDistance(point, a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) return Math.hypot(point.x - a.x, point.y - a.y);
    let t = ((point.x - a.x) * dx + (point.y - a.y) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    const projX = a.x + t * dx;
    const projY = a.y + t * dy;
    return Math.hypot(point.x - projX, point.y - projY);
}

/**
 * @param {Array<{x: number, y: number}>} points
 * @param {Array<{x: number, y: number}>} labelPoints
 * @param {number} [minSep]
 * @returns {number}
 */
export function countLabelProximityHits(points, labelPoints, minSep = MIN_LABEL_SEP) {
    if (!points?.length || !labelPoints?.length) return 0;
    let hits = 0;
    for (const label of labelPoints) {
        if (!label) continue;
        let tooClose = false;
        for (let i = 1; i < points.length; i += 1) {
            if (pointToSegmentDistance(label, points[i - 1], points[i]) < minSep) {
                tooClose = true;
                break;
            }
        }
        if (tooClose) hits += 1;
    }
    return hits;
}

function uniqueTs(preferred) {
    if (preferred == null) return EDGE_TS;
    const values = [preferred, ...EDGE_TS];
    return values.filter((value, index) => values.findIndex((v) => Math.abs(v - value) < 0.01) === index);
}

/**
 * @param {{
 *   points: Array<{x: number, y: number}>,
 *   fromSide: string,
 *   toSide: string,
 *   fromT: number,
 *   toT: number,
 *   foreign: Array<object>,
 *   existingEdges: Array<object>,
 *   labelPoints: Array<{x: number, y: number}>,
 *   fromRect: object,
 *   toRect: object,
 * }} args
 * @returns {object|null}
 */
function scoreStraightRouteCandidate({
    points,
    fromSide,
    toSide,
    fromT,
    toT,
    foreign,
    existingEdges,
    labelPoints,
    fromRect,
    toRect,
    label = null,
    alongSize = null,
}) {
    if (points.length < 2) return null;
    const nodeHits = countNodeHits(points, foreign);
    const endpointHits = countEndpointBodyHits(points, fromRect, toRect);
    const labelHits = countLabelProximityHits(points, labelPoints);
    const edgeHits = countEdgeCrossings(points, existingEdges);
    const obstructionHits = nodeHits + endpointHits + labelHits;
    const minAngle = minAttachmentAngle(points, fromSide, toSide);
    const departSide = facingSide(fromRect, toRect);
    const enterSide = preferredEnterSide(toRect, fromRect);
    let sidePenalty = 0;
    if (fromSide !== departSide) sidePenalty += TRAJECTORY_SIDE_PENALTY;
    if (toSide !== enterSide && toSide !== OPPOSITE_SIDE[departSide]) {
        sidePenalty += TRAJECTORY_SIDE_PENALTY * 0.6;
    }
    const qualityPenalty = attachmentQualityPenalty(points, fromSide, toSide, fromT, toT);
    const score = routeScore(obstructionHits, edgeHits, polylineLength(points), points.length)
        + sidePenalty
        + qualityPenalty
        + labelFitRoutePenalty(points, label, existingEdges, foreign, [fromRect, toRect], alongSize);
    return {
        fromSide,
        toSide,
        fromT,
        toT,
        bend: 0,
        points,
        nodeHits,
        endpointHits,
        labelHits,
        obstructionHits,
        edgeHits,
        length: polylineLength(points),
        minAngle,
        score,
    };
}

/**
 * Search every side/T combination on both ends for the best-scoring straight
 * (bend=0) route. Shared by {@link pickBestStraightRoute} (never falls back
 * to a curve — used when the caller must guarantee a straight line) and
 * {@link pickStraightOrArcArrowRoute} (falls back to a curved arc only when
 * no straight candidate clears obstacles).
 *
 * @param {{
 *   fromRect: object, toRect: object, foreign: Array<object>,
 *   existingEdges: Array<object>, labelPoints: Array<{x: number, y: number}>,
 *   fromTs: number[], toTs: number[],
 * }} args
 * @returns {{best: object|null, bestCompliant: object|null}}
 */
function searchStraightRouteCandidates({
    fromRect,
    toRect,
    foreign,
    existingEdges,
    labelPoints,
    fromTs,
    toTs,
    label = null,
    alongSize = null,
}) {
    let best = null;
    let bestCompliant = null;
    for (const fromSide of ROUTING_SIDES) {
        for (const toSide of ROUTING_SIDES) {
            for (const fromT of fromTs) {
                for (const toT of toTs) {
                    if (isCornerAttachment(fromT) || isCornerAttachment(toT)) continue;
                    const points = buildArcPolyline(fromRect, toRect, {
                        fromSide, toSide, fromT, toT, bend: 0,
                    });
                    const candidate = scoreStraightRouteCandidate({
                        points,
                        fromSide,
                        toSide,
                        fromT,
                        toT,
                        foreign,
                        existingEdges,
                        labelPoints,
                        fromRect,
                        toRect,
                        label,
                        alongSize,
                    });
                    if (!candidate) continue;
                    if (!best || candidate.score < best.score) best = candidate;
                    if (candidate.obstructionHits === 0
                        && routeMeetsAttachmentQuality(candidate)
                        && isBetterAttachmentCandidate(candidate, bestCompliant, fromRect, toRect)) {
                        bestCompliant = candidate;
                    }
                }
            }
        }
    }
    return {best, bestCompliant};
}

/**
 * Best mandatory-straight (bend=0) route, searching all 4 sides on each end
 * — i.e. the arrow is free to reconnect to whichever of the card's
 * left/right/top/bottom edges gives the clearest line, rather than being
 * pinned to the single geometric "facing side" guess. Unlike
 * {@link pickStraightOrArcArrowRoute}, this never falls back to a curved
 * arc: callers that must guarantee a straight line (the Straighten command,
 * straight-edge-style layouts) use this so "straight" always means
 * bend === 0.
 *
 * @param {{
 *   fromRect: {x: number, y: number, w: number, h: number},
 *   toRect: {x: number, y: number, w: number, h: number},
 *   obstacles?: Array<object>,
 *   existingEdges?: Array<object>,
 *   preferredFromT?: number,
 *   preferredToT?: number,
 *   label?: string|null,
 * }} args
 * @returns {object} route with fromSide/toSide/fromT/toT/points/labelPosition/bend=0
 */
export function pickBestStraightRoute({
    fromRect,
    toRect,
    obstacles = [],
    existingEdges = [],
    preferredFromT,
    preferredToT,
    label = null,
}) {
    const foreign = (obstacles || []).filter(
        (rect) => !rectsNearlyEqual(rect, fromRect) && !rectsNearlyEqual(rect, toRect),
    );
    const labelPoints = (existingEdges || [])
        .map((edge) => edge.labelPoint)
        .filter(Boolean);
    const labelBoxes = (existingEdges || [])
        .map((edge) => edge.labelBox)
        .filter(Boolean);
    const fromTs = uniqueTs(preferredFromT);
    const toTs = uniqueTs(preferredToT);
    const alongSize = label ? labelBoxesForPlacement(label).alongSegmentSize : null;

    const {best, bestCompliant} = searchStraightRouteCandidates({
        fromRect, toRect, foreign, existingEdges, labelPoints, fromTs, toTs, label, alongSize,
    });

    let winner = bestCompliant || best;
    if (!winner) {
        // Degenerate rects (zero width/height) — every candidate scored null.
        // Fall back to the deterministic geometric guess so callers always
        // get a usable route rather than throwing.
        const fromSide = facingSide(fromRect, toRect);
        const toSide = preferredEnterSide(toRect, fromRect);
        winner = {
            fromSide,
            toSide,
            fromT: 0.5,
            toT: 0.5,
            bend: 0,
            points: buildArcPolyline(fromRect, toRect, {
                fromSide, toSide, fromT: 0.5, toT: 0.5, bend: 0,
            }),
        };
    }

    return {
        ...winner,
        labelPosition: routeLabelOpts(winner.points, {
            foreign,
            fromRect,
            toRect,
            existingEdges,
            labelPoints,
            label,
            labelBoxes,
        }),
    };
}

export function pickStraightOrArcArrowRoute({
    fromRect,
    toRect,
    obstacles = [],
    existingEdges = [],
    arcIndex = 0,
    preferredFromT,
    preferredToT,
    preferredBendSign = null,
    label = null,
}) {
    const foreign = (obstacles || []).filter(
        (rect) => !rectsNearlyEqual(rect, fromRect) && !rectsNearlyEqual(rect, toRect),
    );
    const labelPoints = (existingEdges || [])
        .map((edge) => edge.labelPoint)
        .filter(Boolean);
    const labelBoxes = (existingEdges || [])
        .map((edge) => edge.labelBox)
        .filter(Boolean);
    const fromTs = uniqueTs(preferredFromT);
    const toTs = uniqueTs(preferredToT);
    const alongSize = label ? labelBoxesForPlacement(label).alongSegmentSize : null;

    const {best, bestCompliant} = searchStraightRouteCandidates({
        fromRect, toRect, foreign, existingEdges, labelPoints, fromTs, toTs, label, alongSize,
    });

    const straight = bestCompliant || (best?.obstructionHits === 0 ? best : null);
    if (straight) {
        return {
            kind: 'arc',
            route: {
                ...straight,
                labelPosition: routeLabelOpts(straight.points, {
                    foreign,
                    fromRect,
                    toRect,
                    existingEdges,
                    labelPoints,
                    label,
                    labelBoxes,
                }),
            },
        };
    }

    return {
        kind: 'arc',
        route: pickArcArrowRoute({
            fromRect,
            toRect,
            obstacles,
            existingEdges,
            arcIndex,
            preferredFromT,
            preferredToT,
            preferredBendSign,
            allowWideDetour: true,
            label,
        }),
    };
}

/**
 * Pick elbow or arc route minimizing card hits, then edge crossings, then length.
 * Elbows try increasing clearance; arcs allow wide detours (e.g. underneath a hub).
 *
 * @param {{
 *   fromRect: {x: number, y: number, w: number, h: number},
 *   toRect: {x: number, y: number, w: number, h: number},
 *   obstacles?: Array<{x: number, y: number, w: number, h: number}>,
 *   existingEdges?: Array<{points: Array<{x: number, y: number}>, strokeWidth?: number}>,
 *   useArc?: boolean,
 *   arcOnly?: boolean,
 *   preferStraight?: boolean,
 *   arcIndex?: number,
 *   preferredFromT?: number,
 *   preferredToT?: number,
 *   label?: string|null,
 *   strokeWidth?: number,
 * }} params
 * @returns {{kind: 'elbow'|'arc', route: object}}
 */
export function pickRoutedArrowRoute({
    fromRect,
    toRect,
    obstacles = [],
    existingEdges = [],
    useArc = true,
    arcOnly = false,
    preferStraight = false,
    arcIndex = 0,
    preferredFromT,
    preferredToT,
    preferredBendSign = null,
    label = null,
    strokeWidth = ARROW_STROKE_WIDTHS.s,
}) {
    if (useArc && preferStraight) {
        return pickStraightOrArcArrowRoute({
            fromRect,
            toRect,
            obstacles,
            existingEdges,
            arcIndex,
            preferredFromT,
            preferredToT,
            preferredBendSign,
            label,
        });
    }

    if (useArc && arcOnly) {
        return {
            kind: 'arc',
            route: pickArcArrowRoute({
                fromRect,
                toRect,
                obstacles,
                existingEdges,
                arcIndex,
                preferredFromT,
                preferredToT,
                preferredBendSign,
                label,
            }),
        };
    }

    let bestKind = 'elbow';
    let bestRoute = pickBestArrowRoute({
        fromRect,
        toRect,
        obstacles,
        existingEdges,
        preferredFromT,
        preferredToT,
        label,
        strokeWidth,
    });

    for (const clearance of ROUTE_CLEARANCE_STEPS.slice(1)) {
        if (fatalRouteHits(bestRoute) === 0) break;
        const candidate = pickBestArrowRoute({
            fromRect,
            toRect,
            obstacles,
            existingEdges,
            clearance,
            preferredFromT,
            preferredToT,
            label,
            strokeWidth,
        });
        if (fatalRouteHits(candidate) < fatalRouteHits(bestRoute)
            || (fatalRouteHits(candidate) === fatalRouteHits(bestRoute)
                && candidate.edgeHits < bestRoute.edgeHits)) {
            bestKind = 'elbow';
            bestRoute = candidate;
        }
    }

    if (useArc) {
        const arcRoute = pickArcArrowRoute({
            fromRect,
            toRect,
            obstacles,
            existingEdges,
            arcIndex,
            preferredFromT,
            preferredToT,
            preferredBendSign,
            label,
        });
        const elbowQuality = routeMeetsAttachmentQuality(bestRoute);
        const arcQuality = routeMeetsAttachmentQuality(arcRoute);
        const arcWinsHits = arcRoute.nodeHits === 0
            && (bestRoute.nodeHits > 0
                || (arcRoute.edgeHits <= bestRoute.edgeHits && arcRoute.length <= bestRoute.length * 1.35));
        const arcWinsDetour = bestRoute.nodeHits > 0 && arcRoute.nodeHits < bestRoute.nodeHits;
        const arcWinsCrossings = bestRoute.nodeHits > 0
            && arcRoute.nodeHits === bestRoute.nodeHits
            && arcRoute.edgeHits < bestRoute.edgeHits;
        if (arcQuality && !elbowQuality) {
            bestKind = 'arc';
            bestRoute = arcRoute;
        } else if (!arcQuality && elbowQuality) {
            // keep elbow
        } else if (arcWinsHits || arcWinsDetour || arcWinsCrossings) {
            bestKind = 'arc';
            bestRoute = arcRoute;
        }
    }

    return {kind: bestKind, route: bestRoute};
}
